import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, Wrench, BarChart3, Check } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import ContactForm from '../components/ContactForm';
import { useLang } from '../context/LanguageContext';

const SERVICE_ICONS = [ClipboardList, Wrench, BarChart3];

export default function Management() {
  const { t } = useLang();
  const m = t.management;

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="px-6 md:px-12 lg:px-16 py-16 lg:py-28 flex flex-col justify-center">
            <SectionLabel>{m.hero_label}</SectionLabel>
            <h1 className="mt-8 text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              {m.hero_title}
            </h1>
            <p className="mt-8 text-[15px] leading-relaxed text-neutral-700 max-w-lg">
              {m.hero_text}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#request"
                className="group inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-sm tracking-[0.15em] hover:bg-[#e11f3a] transition-colors"
              >
                {m.cta}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
              >
                {t.hero.cta_contact}
              </Link>
            </div>
          </div>
          <div className="relative min-h-[420px] lg:min-h-[640px] bg-neutral-100">
            <img
              src="/assets/whoweare/living.webp"
              alt="Property management"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur px-6 py-5 shadow-lg">
              <p className="text-[11px] tracking-[0.25em] text-neutral-500">{m.badge_years}</p>
              <p className="text-lg font-semibold mt-1">{m.badge_tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>{m.services_label}</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight max-w-2xl">
            {m.services_title}
          </h2>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {m.services.map((s, i) => {
              const Icon = SERVICE_ICONS[i] || ClipboardList;
              return (
                <div key={s.title} className="bg-neutral-50 p-8 lg:p-10 border-t-2 border-[#e11f3a] h-full">
                  <div className="w-12 h-12 bg-white border border-black flex items-center justify-center">
                    <Icon className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold">{s.title}</h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.text}</p>
                  {s.items && (
                    <ul className="mt-6 space-y-2.5">
                      {s.items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-sm text-neutral-700">
                          <span className="mt-1.5 w-1.5 h-1.5 bg-[#e11f3a] flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-neutral-50 px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto">
          <SectionLabel>{m.process_label}</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight max-w-2xl">
            {m.process_title}
          </h2>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {m.process.map((p) => (
              <div key={p.step} className="bg-white p-8 border-t-2 border-[#e11f3a]">
                <span className="text-4xl font-light text-neutral-300">{p.step}</span>
                <h3 className="mt-4 text-lg font-semibold">{p.title}</h3>
                <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <SectionLabel>{m.fees_label}</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight">
              {m.fees_title}
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-neutral-700 max-w-lg">
              {m.fees_text}
            </p>
            <ul className="mt-8 space-y-3">
              {m.commitments.map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-neutral-700">
                  <span className="w-6 h-6 border border-[#e11f3a] flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-[#e11f3a]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-black text-white p-10 lg:p-14">
            <p className="text-[11px] tracking-[0.3em] text-neutral-400">{m.promise_label}</p>
            <h3 className="mt-4 text-2xl md:text-3xl font-light leading-tight">
              {m.promise_title}
            </h3>
            <p className="mt-6 text-neutral-300 leading-relaxed">
              {m.promise_text}
            </p>
            <a
              href="#request"
              className="mt-8 inline-flex items-center gap-2 border border-white/40 px-6 py-3 text-sm tracking-[0.15em] hover:bg-white hover:text-black transition-colors"
            >
              {m.cta}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA / CONTACT */}
      <section id="request" className="bg-neutral-50 px-6 md:px-12 lg:px-16 py-24">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16">
          <div>
            <SectionLabel>{t.sections.contact_us}</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-light tracking-tight">
              {m.cta_section_title}
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-neutral-700 max-w-lg">
              {m.cta_section_text}
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <div>
                <span className="text-neutral-500 tracking-[0.2em] text-xs">{t.sections.email_label}</span>
                <p className="mt-1 text-black">gestion@imargo.be</p>
              </div>
              <div>
                <span className="text-neutral-500 tracking-[0.2em] text-xs">{t.sections.phone_label}</span>
                <p className="mt-1 text-black">02 644 10 20</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}
